import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { useAuth } from "../../app/AuthContext";
import { navigate } from "../../app/routes";
import { ApiError } from "../../shared/api/client";
import type {
  AttachmentRead,
  ConversationMemberRead,
  ConversationRead,
  DirectoryUserRead,
  FeedbackRating,
  KnowledgeCardDetail,
  MessageRead,
  ResponseMode,
  RetrievalRunRead,
  RetrievalProfileRead,
} from "../../shared/api/types";
import { formatDateTime, formatFileSize, formatRelativeDate, truncate } from "../../shared/lib/format";
import { canUserWriteConversation } from "../../shared/lib/conversations";
import { copyText } from "../../shared/lib/clipboard";
import { collectPermissions } from "../../shared/lib/permissions";
import { Button, Badge, Callout, EmptyState, IconButton, Input, Modal, SegmentedControl, Select, Spinner, Textarea } from "../../shared/ui/Primitives";
import { Icon } from "../../shared/ui/Icon";
import { citedKnowledgeIds, Markdown } from "../../shared/ui/Markdown";
import { JsonView } from "../../shared/ui/JsonView";
import { useToast } from "../../shared/ui/Toast";
import { CardDetail } from "../knowledge/CardDetail";
import { ConversationSettings } from "./ConversationSettings";
import { feedbackOptions, feedbackReasons, responseModeOptions, visibilityOptions } from "./constants";

interface PendingAttachment extends AttachmentRead { localName: string; }

interface KnowledgePreviewState {
  ids: string[];
  index: number;
  snapshotId: string | null;
}

export function ChatScreen({ conversationId }: { conversationId?: string }) {
  const { api, user } = useAuth();
  const { showToast } = useToast();
  async function copyAnswer(text: string) {
    try {
      await copyText(text);
      showToast("Ответ скопирован", "success");
    } catch {
      showToast("Не удалось скопировать ответ. Выделите текст и скопируйте через меню браузера или Ctrl+C (⌘C на Mac).", "danger");
    }
  }
  const permissions = useMemo(() => collectPermissions(user), [user]);
  const [conversations, setConversations] = useState<ConversationRead[]>([]);
  const [totalConversations, setTotalConversations] = useState(0);
  const [conversation, setConversation] = useState<ConversationRead | null>(null);
  const [messages, setMessages] = useState<MessageRead[]>([]);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [archived, setArchived] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [sending, setSending] = useState(false);
  const [content, setContent] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(0);
  const [profiles, setProfiles] = useState<RetrievalProfileRead[]>([]);
  const [users, setUsers] = useState<DirectoryUserRead[]>([]);
  const [members, setMembers] = useState<ConversationMemberRead[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<MessageRead | null>(null);
  const [feedbackRating, setFeedbackRating] = useState<FeedbackRating>("useful");
  const [feedbackReason, setFeedbackReason] = useState("");
  const [feedbackComment, setFeedbackComment] = useState("");
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);
  const [knowledgePreview, setKnowledgePreview] = useState<KnowledgePreviewState | null>(null);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const composerTextareaRef = useRef<HTMLTextAreaElement>(null);

  const loadConversations = useCallback(async () => {
    setLoadingList(true);
    try {
      const page = await api.listConversations({ archived, q: search.trim() || undefined, limit: 100 });
      setConversations(page.items);
      setTotalConversations(page.total);
      if (!conversationId && page.items[0]) navigate({ name: "chat", conversationId: page.items[0].id }, true);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось загрузить беседы", "danger");
    } finally {
      setLoadingList(false);
    }
  }, [api, archived, search, conversationId, showToast]);

  useEffect(() => { const timer = window.setTimeout(() => void loadConversations(), 250); return () => window.clearTimeout(timer); }, [loadConversations]);

  useEffect(() => {
    void api.listRetrievalProfiles().then(setProfiles).catch(() => setProfiles([]));
    if (permissions.has("conversation.share") || permissions.has("conversation.transfer")) {
      void api.listDirectoryUsers({ active: true, limit: 200 }).then(setUsers).catch(() => setUsers([]));
    } else {
      setUsers([]);
    }
  }, [api, permissions]);

  useEffect(() => {
    if (!conversationId) { setConversation(null); setMessages([]); setMembers([]); return; }
    let active = true;
    setLoadingConversation(true);
    setMembersLoading(true);
    Promise.all([api.getConversation(conversationId), api.listMessages(conversationId)])
      .then(([currentConversation, currentMessages]) => {
        if (!active) return;
        setConversation(currentConversation);
        setMessages(currentMessages);
        const lastAssistant = [...currentMessages].reverse().find((item) => item.role === "assistant");
        setSelectedMessageId(lastAssistant?.id ?? null);
      })
      .catch((error) => active && showToast(error instanceof Error ? error.message : "Не удалось открыть беседу", "danger"))
      .finally(() => active && setLoadingConversation(false));
    void api.listConversationMembers(conversationId)
      .then((items) => { if (active) setMembers(items); })
      .catch((error) => {
        if (active) {
          setMembers([]);
          showToast(error instanceof Error ? error.message : "Не удалось загрузить участников беседы", "danger");
        }
      })
      .finally(() => { if (active) setMembersLoading(false); });
    return () => { active = false; };
  }, [api, conversationId, showToast]);

  useEffect(() => { messageEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, sending]);

  useLayoutEffect(() => {
    const textarea = composerTextareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    const nextHeight = Math.min(textarea.scrollHeight, 180);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > 180 ? "auto" : "hidden";
  }, [content]);

  const selectedMessage = useMemo(
    () => messages.find((item) => item.id === selectedMessageId) ?? [...messages].reverse().find((item) => item.role === "assistant") ?? null,
    [messages, selectedMessageId],
  );
  const canWriteConversation = useMemo(() => {
    return canUserWriteConversation({
      conversation,
      userId: user?.id,
      members,
      canViewAll: permissions.has("conversation.view_all"),
    });
  }, [conversation, members, permissions, user]);

  const openKnowledgePreview = useCallback((id: string, ids: string[] = [id], snapshotId: string | null = null) => {
    const uniqueIds = [...new Set([...ids, id])];
    setKnowledgePreview({ ids: uniqueIds, index: uniqueIds.indexOf(id), snapshotId });
  }, []);

  const selectKnowledgePreviewIndex = useCallback((index: number) => {
    setKnowledgePreview((current) => current ? { ...current, index } : null);
  }, []);

  const closeKnowledgePreview = useCallback(() => setKnowledgePreview(null), []);

  const openRelatedKnowledgeCard = useCallback((id: string) => {
    setKnowledgePreview((current) => {
      if (!current) return { ids: [id], index: 0, snapshotId: null };
      const existingIndex = current.ids.indexOf(id);
      if (existingIndex >= 0) return { ...current, index: existingIndex };
      return { ...current, ids: [...current.ids, id], index: current.ids.length };
    });
  }, []);

  async function createConversation() {
    try {
      const created = await api.createConversation({ title: "Новая беседа", response_mode: "analysis" });
      setConversations((current) => [created, ...current]);
      navigate({ name: "chat", conversationId: created.id });
      setLeftOpen(false);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось создать беседу", "danger");
    }
  }

  async function uploadFiles(event: ChangeEvent<HTMLInputElement>) {
    if (!conversation) return;
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!files.length) return;
    setUploadingAttachments((value) => value + files.length);
    await Promise.all(files.map(async (file) => {
      try {
        const uploaded = await api.uploadAttachment(conversation.id, file);
        setPendingAttachments((current) => [...current, { ...uploaded, localName: file.name }]);
      } catch (error) {
        showToast(`${file.name}: ${error instanceof Error ? error.message : "ошибка загрузки"}`, "danger");
      } finally {
        setUploadingAttachments((value) => Math.max(0, value - 1));
      }
    }));
  }

  async function send(event: FormEvent) {
    event.preventDefault();
    if (!conversation || !canWriteConversation || !content.trim() || sending || uploadingAttachments) return;
    const question = content.trim();
    const wasEmpty = messages.length === 0;
    setSending(true);
    try {
      const response = await api.sendMessage(conversation.id, {
        content: question,
        response_mode: conversation.response_mode,
        retrieval_profile_id: conversation.retrieval_profile_id,
        attachment_ids: pendingAttachments.map((item) => item.id),
      });
      setMessages((current) => [...current, response.user_message, response.assistant_message]);
      setSelectedMessageId(response.assistant_message.id);
      setContent("");
      setPendingAttachments([]);
      setRightOpen(true);
      if (wasEmpty && conversation.title === "Новая беседа") {
        const updated = await api.updateConversation(conversation.id, { title: truncate(question, 70) });
        setConversation(updated);
        setConversations((current) => current.map((item) => item.id === updated.id ? updated : item));
      } else {
        await loadConversations();
      }
    } catch (error) {
      const message = error instanceof ApiError && error.status === 503
        ? `Модель или поиск временно недоступны: ${error.message}`
        : error instanceof Error ? error.message : "Не удалось получить ответ";
      showToast(message, "danger");
    } finally {
      setSending(false);
    }
  }

  async function updateConversation(payload: Parameters<typeof api.updateConversation>[1]) {
    if (!conversation) return;
    const updated = await api.updateConversation(conversation.id, payload);
    setConversation(updated);
    setConversations((current) => current.map((item) => item.id === updated.id ? updated : item));
    showToast("Настройки беседы сохранены", "success");
  }

  async function archiveConversation() {
    if (!conversation) return;
    try {
      await updateConversation({ archived: true });
      setConversation(null);
      setMessages([]);
      navigate({ name: "chat" });
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось архивировать беседу", "danger");
    }
  }

  async function reloadMembers() {
    if (!conversation) return;
    setMembersLoading(true);
    try {
      setMembers(await api.listConversationMembers(conversation.id));
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось обновить список участников", "danger");
    } finally {
      setMembersLoading(false);
    }
  }

  async function saveFeedback() {
    if (!feedbackMessage) return;
    setSavingFeedback(true);
    try {
      await api.setFeedback(feedbackMessage.id, {
        rating: feedbackRating,
        reason: feedbackReason || null,
        comment: feedbackComment.trim() || null,
      });
      showToast("Оценка сохранена", "success");
      setFeedbackMessage(null);
      setFeedbackComment("");
      setFeedbackReason("");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось сохранить оценку", "danger");
    } finally {
      setSavingFeedback(false);
    }
  }

  return (
    <div className="chat-page">
      <aside className={`conversation-list ${leftOpen ? "is-open" : ""}`}>
        <header className="panel-header">
          <div><h1>Беседы</h1><span>{totalConversations}</span></div>
          <Button size="small" icon="plus" onClick={() => void createConversation()}>Новая</Button>
        </header>
        <div className="conversation-tools">
          <label className="search-box"><Icon name="search" size={17} /><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Поиск по беседам" /></label>
          <button className={`archive-toggle ${archived ? "is-active" : ""}`} type="button" onClick={() => setArchived((value) => !value)}><Icon name="archive" size={17} />{archived ? "Архив" : "Текущие"}</button>
        </div>
        <div className="conversation-items">
          {loadingList ? Array.from({ length: 5 }, (_, index) => <div className="conversation-skeleton" key={index} />) : null}
          {!loadingList && !conversations.length ? <EmptyState title={archived ? "Архив пуст" : "Нет бесед"} description={search ? "Измените поисковый запрос." : "Создайте первую беседу с помощником."} icon="chat" /> : null}
          {conversations.map((item) => (
            <button key={item.id} type="button" className={`conversation-item ${item.id === conversationId ? "is-active" : ""}`} onClick={() => { navigate({ name: "chat", conversationId: item.id }); setLeftOpen(false); }}>
              <strong>{item.title}</strong>
              <span>{formatRelativeDate(item.updated_at)}</span>
              <small>{visibilityOptions.find((option) => option.value === item.visibility)?.label}</small>
            </button>
          ))}
        </div>
      </aside>
      {leftOpen ? <button className="pane-scrim" aria-label="Закрыть список бесед" onClick={() => setLeftOpen(false)} /> : null}

      <section className="chat-main">
        {conversation ? (
          <>
            <header className="chat-header">
              <IconButton className="chat-header__mobile" icon="menu" label="Беседы" onClick={() => setLeftOpen(true)} />
              <div className="chat-header__title"><h2>{conversation.title}</h2><div><Badge tone={conversation.archived ? "warning" : "neutral"}>{visibilityOptions.find((option) => option.value === conversation.visibility)?.label}</Badge><span>{responseModeOptions.find((option) => option.value === conversation.response_mode)?.label}</span></div></div>
              <div className="chat-header__actions">
                <IconButton icon="panel" label="Показать сведения об ответе" onClick={() => setRightOpen(true)} />
                <IconButton icon="settings" label="Настройки беседы" disabled={!canWriteConversation} onClick={() => setSettingsOpen(true)} />
                <IconButton icon="archive" label="Архивировать" disabled={!canWriteConversation} onClick={() => void archiveConversation()} />
              </div>
            </header>
            <div className="message-list">
              {loadingConversation ? <div className="centered"><Spinner /><span>Загружаем сообщения…</span></div> : null}
              {!loadingConversation && !messages.length ? (
                <div className="chat-welcome">
                  <span className="brand-mark">S</span>
                  <h2>Что нужно выяснить?</h2>
                  <p>Опишите вопрос клиента. Помощник найдёт карточки базы знаний, учтёт связи между ними и подготовит внутренний анализ.</p>
                  <div className="prompt-suggestions">
                    {["Как подобрать УЗИП для шкафа наружного освещения?", "Чем рекомендуемый предохранитель отличается от SCCR?", "Проверь решение для сети TN-S, 230 В"].map((text) => <button key={text} type="button" onClick={() => setContent(text)}>{text}</button>)}
                  </div>
                </div>
              ) : null}
              {messages.map((message) => (
                <article key={message.id} className={`message message--${message.role} ${message.id === selectedMessageId ? "is-selected" : ""}`} onClick={() => message.role === "assistant" && setSelectedMessageId(message.id)}>
                  <div className="message__avatar">{message.role === "assistant" ? "S" : (user?.first_name?.[0] ?? "Я")}</div>
                  <div className="message__body">
                    <header><strong>{message.role === "assistant" ? "ИИ-помощник" : "Вы"}</strong><span>{formatDateTime(message.created_at)}</span>{message.response_mode ? <Badge>{responseModeOptions.find((option) => option.value === message.response_mode)?.label}</Badge> : null}</header>
                    <Markdown text={message.content} onKnowledgeIdClick={(id) => openKnowledgePreview(id, citedKnowledgeIds(message.content), message.knowledge_snapshot_id)} />
                    {message.attachments.length ? <div className="message-attachments">{message.attachments.map((item) => <span key={item.id}><Icon name="paperclip" size={15} />{item.original_filename}</span>)}</div> : null}
                    {message.role === "assistant" ? <footer className="message__actions"><IconButton icon="copy" label="Скопировать ответ" onClick={(event: ReactMouseEvent<HTMLButtonElement>) => { event.stopPropagation(); void copyAnswer(message.content); }} /><IconButton icon="thumbUp" label="Оценить ответ" onClick={(event: ReactMouseEvent<HTMLButtonElement>) => { event.stopPropagation(); setFeedbackMessage(message); setFeedbackRating("useful"); }} /><span>{message.model_name}</span></footer> : null}
                  </div>
                </article>
              ))}
              {sending ? <article className="message message--assistant"><div className="message__avatar">S</div><div className="message__body"><header><strong>ИИ-помощник</strong></header><div className="typing"><span/><span/><span/></div><p className="muted">Ищем знания и готовим ответ…</p></div></article> : null}
              <div ref={messageEndRef} />
            </div>
            <form className="composer" onSubmit={send}>
              {!canWriteConversation && !membersLoading ? <Callout tone="info">Беседа доступна только для чтения. Владелец может выдать право продолжать диалог.</Callout> : null}
              {pendingAttachments.length || uploadingAttachments ? <div className="pending-attachments">{pendingAttachments.map((item) => <span key={item.id}><Icon name="paperclip" size={15} /><span>{item.original_filename}</span><small>{item.extraction_status === "completed" ? "текст готов" : "распознаётся"}</small><button type="button" aria-label="Убрать вложение" onClick={() => setPendingAttachments((current) => current.filter((candidate) => candidate.id !== item.id))}>×</button></span>)}{uploadingAttachments ? <span><Spinner /><span>Загрузка…</span></span> : null}</div> : null}
              <div className="composer__controls">
                <label className="attachment-button" title="Прикрепить файл"><Icon name="paperclip" size={20} /><input type="file" multiple disabled={!canWriteConversation} onChange={(event: ChangeEvent<HTMLInputElement>) => void uploadFiles(event)} /></label>
                <Textarea ref={composerTextareaRef} disabled={!canWriteConversation} value={content} onChange={(event) => setContent(event.target.value)} placeholder={canWriteConversation ? "Введите технический вопрос…" : "Беседа доступна только для чтения"} rows={1} onKeyDown={(event: ReactKeyboardEvent<HTMLTextAreaElement>) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); } }} />
                <Button type="submit" icon="send" disabled={!canWriteConversation || !content.trim() || sending || Boolean(uploadingAttachments)} aria-label="Отправить">Отправить</Button>
              </div>
              <div className="composer__footer"><span>Enter — отправить, Shift+Enter — новая строка</span>{canWriteConversation ? <SegmentedControl value={conversation.response_mode} options={responseModeOptions.map(({ value, label }) => ({ value, label }))} onChange={(mode: ResponseMode) => void updateConversation({ response_mode: mode }).catch((error) => showToast(error instanceof Error ? error.message : "Не удалось изменить режим ответа", "danger"))} ariaLabel="Режим ответа" /> : <Badge>{responseModeOptions.find((option) => option.value === conversation.response_mode)?.label}</Badge>}</div>
            </form>
          </>
        ) : (
          <EmptyState title="Выберите беседу" description="Откройте существующую беседу или создайте новую." icon="chat" action={<Button icon="plus" onClick={() => void createConversation()}>Новая беседа</Button>} />
        )}
      </section>

      <aside className={`insight-panel ${rightOpen ? "is-open" : ""}`}>
        <header className="panel-header"><div><h2>Обоснование</h2><span>ответ помощника</span></div><IconButton className="mobile-only" icon="close" label="Закрыть" onClick={() => setRightOpen(false)} /></header>
        {selectedMessage ? <InsightContent message={selectedMessage} onOpenCard={(id, ids) => openKnowledgePreview(id, ids, selectedMessage.knowledge_snapshot_id)} /> : <EmptyState title="Нет выбранного ответа" description="Выберите ответ помощника в центре." icon="info" />}
      </aside>
      {rightOpen ? <button className="pane-scrim pane-scrim--right" aria-label="Закрыть сведения" onClick={() => setRightOpen(false)} /> : null}

      {conversation ? (
        <ConversationSettings
          open={settingsOpen}
          conversation={conversation}
          profiles={profiles}
          users={users}
          members={members}
          membersLoading={membersLoading}
          canShare={canWriteConversation && permissions.has("conversation.share")}
          canTransfer={canWriteConversation && permissions.has("conversation.transfer")}
          onClose={() => setSettingsOpen(false)}
          onSave={updateConversation}
          onShare={async (userId, access) => {
            await api.shareConversation(conversation.id, userId, access);
            await reloadMembers();
            showToast("Доступ сохранён", "success");
          }}
          onRemoveMember={async (userId) => {
            await api.removeConversationMember(conversation.id, userId);
            await reloadMembers();
            showToast("Участник удалён", "success");
          }}
          onTransfer={async (userId) => {
            const updated = await api.transferConversation(conversation.id, userId);
            setConversation(updated);
            setConversations((current) => current.map((item) => item.id === updated.id ? updated : item));
            await reloadMembers();
            showToast("Беседа передана", "success");
          }}
        />
      ) : null}

      <Modal open={Boolean(feedbackMessage)} title="Оценить ответ" onClose={() => setFeedbackMessage(null)} footer={<><Button variant="secondary" onClick={() => setFeedbackMessage(null)}>Отмена</Button><Button disabled={savingFeedback} onClick={() => void saveFeedback()}>{savingFeedback ? "Сохраняем…" : "Сохранить"}</Button></>}>
        <div className="stack">
          <SegmentedControl value={feedbackRating} options={feedbackOptions} onChange={setFeedbackRating} ariaLabel="Оценка ответа" />
          <label className="field"><span className="field__label">Причина</span><Select value={feedbackReason} onChange={(event) => setFeedbackReason(event.target.value)}><option value="">Не указывать</option>{feedbackReasons.map((reason) => <option key={reason}>{reason}</option>)}</Select></label>
          <label className="field"><span className="field__label">Комментарий</span><Textarea rows={4} value={feedbackComment} onChange={(event) => setFeedbackComment(event.target.value)} placeholder="Что именно нужно улучшить?" /></label>
        </div>
      </Modal>
      <KnowledgePreviewModal
        state={knowledgePreview}
        onClose={closeKnowledgePreview}
        onMove={selectKnowledgePreviewIndex}
        onOpenRelated={openRelatedKnowledgeCard}
      />
    </div>
  );
}

function KnowledgePreviewModal({
  state,
  onClose,
  onMove,
  onOpenRelated,
}: {
  state: KnowledgePreviewState | null;
  onClose: () => void;
  onMove: (index: number) => void;
  onOpenRelated: (id: string) => void;
}) {
  const { api } = useAuth();
  const [detail, setDetail] = useState<KnowledgeCardDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const currentId = state?.ids[state.index] ?? null;

  useEffect(() => {
    let active = true;
    setDetail(null);
    setError("");
    if (!currentId) return () => { active = false; };
    setLoading(true);
    void api.getCard(currentId, state?.snapshotId)
      .then((value) => { if (active) setDetail(value); })
      .catch((loadError) => {
        if (active) setError(loadError instanceof Error ? loadError.message : "Не удалось загрузить карточку");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, currentId, state?.snapshotId]);

  useEffect(() => {
    if (!state || state.ids.length < 2) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" && state.index > 0) onMove(state.index - 1);
      if (event.key === "ArrowRight" && state.index < state.ids.length - 1) onMove(state.index + 1);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onMove, state]);

  const footer = state && state.ids.length > 1 ? (
    <div className="knowledge-preview__navigation">
      <Button variant="secondary" disabled={state.index === 0} onClick={() => onMove(state.index - 1)}>← Предыдущая</Button>
      <span>{state.index + 1} из {state.ids.length}</span>
      <Button variant="secondary" disabled={state.index === state.ids.length - 1} onClick={() => onMove(state.index + 1)}>Следующая →</Button>
    </div>
  ) : undefined;

  return (
    <Modal
      open={Boolean(state)}
      title={detail?.title || currentId || "Карточка базы знаний"}
      onClose={onClose}
      footer={footer}
      wide
    >
      {loading ? <div className="centered"><Spinner /><span>Загружаем карточку…</span></div> : null}
      {error ? <Callout tone="danger">{error}</Callout> : null}
      {!loading && detail ? <CardDetail detail={detail} onOpenCard={onOpenRelated} /> : null}
    </Modal>
  );
}

function InsightContent({ message, onOpenCard }: { message: MessageRead; onOpenCard: (id: string, ids: string[]) => void }) {
  const { api } = useAuth();
  const [run, setRun] = useState<RetrievalRunRead | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const citedIds = citedKnowledgeIds(message.content);

  useEffect(() => {
    let active = true;
    setRun(null);
    setError("");
    setLoading(false);
    if (!message.retrieval_run_id) return () => { active = false; };
    setLoading(true);
    void api.getRetrievalRun(message.retrieval_run_id)
      .then((value) => { if (active) setRun(value); })
      .catch((loadError) => {
        if (active) setError(loadError instanceof Error ? loadError.message : "Не удалось загрузить трассировку поиска");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, message.retrieval_run_id]);

  const included = run?.items.filter((item) => item.included_in_context) ?? [];
  const excluded = run?.items.filter((item) => !item.included_in_context) ?? [];
  const stats = run?.stats ?? (message.metadata_json.retrieval_stats ?? {}) as Record<string, unknown>;

  return (
    <div className="insight-content">
      {loading ? <div className="centered centered--compact"><Spinner /><span>Загружаем трассировку…</span></div> : null}
      {error ? <Callout tone="warning">{error}</Callout> : null}

      {run ? (
        <>
          <section>
            <div className="section-title-row section-title-row--compact">
              <h3>Карточки в контексте модели</h3>
              <Badge tone="success">{included.length}</Badge>
            </div>
            {included.length ? <RetrievalTraceItems items={included} onOpenCard={onOpenCard} /> : <p className="muted">Контекст не содержит карточек базы знаний.</p>}
          </section>
          {excluded.length ? (
            <section>
              <details>
                <summary>Найдены, но не вошли в контекст ({excluded.length})</summary>
                <RetrievalTraceItems items={excluded} onOpenCard={onOpenCard} compact />
              </details>
            </section>
          ) : null}
          <section>
            <details>
              <summary>Нормализованный запрос</summary>
              <JsonView value={run.normalized_query} />
            </details>
          </section>
          <section>
            <details>
              <summary>Контекст, переданный модели</summary>
              <pre className="context-preview context-preview--panel">{run.context_text}</pre>
            </details>
          </section>
        </>
      ) : !loading ? (
        <section>
          <h3>Карточки, упомянутые в ответе</h3>
          {citedIds.length ? (
            <div className="knowledge-id-list">
              {citedIds.map((id) => (
                <button key={id} type="button" onClick={() => onOpenCard(id, citedIds)}>
                  <Icon name="database" size={16} /><span>{id}</span><Icon name="chevron" size={15} />
                </button>
              ))}
            </div>
          ) : <p className="muted">У ответа нет retrieval-run и явных номеров карточек.</p>}
        </section>
      ) : null}

      <section>
        <h3>Параметры ответа</h3>
        <dl className="meta-list">
          <div><dt>Модель</dt><dd>{message.model_name || "—"}</dd></div>
          <div><dt>Снимок базы</dt><dd>{message.knowledge_snapshot_id || run?.snapshot_id || "—"}</dd></div>
          <div><dt>Версия профиля</dt><dd>{message.retrieval_profile_version_id || run?.profile_version_id || "—"}</dd></div>
          <div><dt>Версия промпта</dt><dd>{message.prompt_version_id || "—"}</dd></div>
          <div><dt>Время ответа</dt><dd>{formatDuration(message.latency_ms ?? run?.duration_ms)}</dd></div>
          <div><dt>Стоимость провайдера</dt><dd>{formatProviderCost(message.provider_cost)}</dd></div>
          <div><dt>Retrieval run</dt><dd>{message.retrieval_run_id || "—"}</dd></div>
        </dl>
      </section>
      <section><h3>Статистика поиска</h3><JsonView value={stats} /></section>
      <section><h3>Использование токенов</h3><JsonView value={message.token_usage} /></section>
    </div>
  );
}

function RetrievalTraceItems({
  items,
  onOpenCard,
  compact = false,
}: {
  items: RetrievalRunRead["items"];
  onOpenCard: (id: string, ids: string[]) => void;
  compact?: boolean;
}) {
  const cardIds = [...new Set(items.map((item) => item.external_id))];
  return (
    <div className={`retrieval-trace-list ${compact ? "retrieval-trace-list--compact" : ""}`}>
      {items.map((item) => (
        <article className="retrieval-trace-item" key={`${item.rank}-${item.card_version_id}`}>
          <button type="button" className="retrieval-trace-item__main" onClick={() => onOpenCard(item.external_id, cardIds)}>
            <div>
              <strong>{item.external_id}</strong>
              <Badge tone={item.reviewed ? "success" : ["draft", "черновик"].includes(item.status.toLowerCase()) ? "warning" : "neutral"}>
                {item.reviewed ? "Проверено" : item.status}
              </Badge>
            </div>
            <span>{item.title}</span>
            <small>
              #{item.rank} · {item.source_channel}
              {item.score === null ? "" : ` · ${item.score.toFixed(4)}`}
              {item.context_chunk_ids.length ? ` · чанков: ${item.context_chunk_ids.length}` : ""}
            </small>
          </button>
          {item.relation_path.length ? (
            <details>
              <summary>Путь по связям</summary>
              <JsonView value={item.relation_path} />
            </details>
          ) : null}
        </article>
      ))}
    </div>
  );
}

function formatDuration(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return value < 1_000 ? `${Math.round(value)} мс` : `${(value / 1_000).toFixed(2)} с`;
}

function formatProviderCost(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `$${value.toFixed(value < 0.01 ? 6 : 4)}`;
}
